import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  useAuthentication,
  useMarkNotificationsAsRead,
  useRetrieveNotifications,
  useRetrieveUnReadNotificationsNbr,
} from "@planzone/view-models";
import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { Props } from "./types";
import { Icon } from "./icon";
import { Notification } from "./notification";
import { Loader } from "./loader";
import styles from "./styles.scss";

function Notifications(props: Props) {
  const { account } = useAuthentication();
  const { t } = useTranslation();
  const push = useNavigate();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    filter,
    notifications,
    totalPages,
    retrieveNotifications,
  } = useRetrieveNotifications();
  const { nbr, retrieveUnReadNotificationsNbr } =
    useRetrieveUnReadNotificationsNbr();
  const { markNotificationsAsRead } = useMarkNotificationsAsRead();

  useMessage(({ type }) => {
    if (
      type === events.postCommentSucceeded ||
      type === events.allocateResourcesSucceeded ||
      type === events.addPlanzoneResourcesToProjectSucceeded ||
      type === events.createEventSucceeded ||
      type === events.editEventSucceeded ||
      type === events.editTaskSucceeded ||
      type === events.createTaskSucceeded
    ) {
      setTimeout(() => {
        retrieveUnReadNotificationsNbr(account.resource);
        retrieveNotifications({
          filter: { ...filter, start: 0 },
          resource: account.resource,
        });
      }, 5000);
    }
  });

  return (
    <VisibilityWatcher blur={() => markNotificationsAsRead([])}>
      {(isVisible, show, hide) => (
        <div className={styles.notifications}>
          <div className={styles.iconBox} onClick={isVisible ? hide : show}>
            <Icon className={styles.icon} />
            {nbr > 0 && <div className={styles.notificationMark} />}
          </div>
          {isVisible && (
            <div className={styles.popup}>
              <div className={styles.list}>
                <InfiniteScroll
                  canFetchOnIdle={
                    !isRequestPending &&
                    !isRequestSuccess &&
                    !isRequestFailure &&
                    notifications.length === 0
                  }
                >
                  {() => (
                    <div className={styles.main}>
                      <React.Fragment>
                        {notifications.length > 0 ? (
                          notifications.map((notification) => (
                            <Notification
                              key={notification.uuid}
                              notification={notification}
                              close={hide}
                            />
                          ))
                        ) : isRequestPending ? (
                          <>
                            <Loader />
                            <Loader />
                          </>
                        ) : (
                          <p className={styles.placeholder}>
                            {t("notifications.placeholder")}
                          </p>
                        )}
                      </React.Fragment>
                      <Flag
                        name="zonesFlag"
                        retrieveItems={() =>
                          !isRequestPending &&
                          retrieveNotifications({
                            filter,
                            resource: account.resource,
                          })
                        }
                        isDisabled={
                          isRequestPending ||
                          (filter.start > 0 &&
                            totalPages > 0 &&
                            filter.start >= totalPages)
                        }
                      />
                    </div>
                  )}
                </InfiniteScroll>
              </div>
              <div
                className={styles.seeAll}
                onClick={() => {
                  push("/notifications");
                  hide();
                }}
              >
                <p>{t("notifications.seeAll")}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </VisibilityWatcher>
  );
}

export { Notifications };
